import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Select, message, Spin, Row, Col, Divider } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

export default function Profile() {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/auth/profile');
        profileForm.setFieldsValue(res.data.data);
      } catch {
        message.error('Failed to load profile');
      }
      setLoading(false);
    })();
  }, [profileForm]);

  const handleProfileSave = async (values) => {
    setSaving(true);
    try {
      const res = await api.put('/api/auth/profile', values);
      message.success('Profile updated');
      if (res.data.data?.username) {
        localStorage.setItem('username', res.data.data.username);
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update profile');
    }
    setSaving(false);
  };

  const handlePasswordChange = async (values) => {
    setChangingPw(true);
    try {
      await api.put('/api/auth/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success('Password changed');
      passwordForm.resetFields();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to change password');
    }
    setChangingPw(false);
  };

  return (
    <PageLayout>
      <div className="page-header">
        <h1>My Profile</h1>
        <p>Manage your account settings</p>
      </div>

      <Spin spinning={loading}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card title="Profile Information">
              <Form form={profileForm} layout="vertical" onFinish={handleProfileSave}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: 'Required' }]}>
                      <Input prefix={<UserOutlined />} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: 'Required' }]}>
                      <Input prefix={<UserOutlined />} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="email" label="Email">
                  <Input prefix={<MailOutlined />} disabled />
                </Form.Item>
                <Form.Item name="phone" label="Phone" extra="E.164 format, e.g. +61412345678">
                  <Input prefix={<PhoneOutlined />} placeholder="+61..." />
                </Form.Item>
                <Form.Item name="twofaMethod" label="2FA Method">
                  <Select
                    options={[
                      { value: 'email', label: 'Email' },
                      { value: 'sms', label: 'SMS' },
                    ]}
                  />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Save Changes
                </Button>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Change Password">
              <Form form={passwordForm} layout="vertical" onFinish={handlePasswordChange}>
                <Form.Item
                  name="currentPassword"
                  label="Current Password"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>
                <Divider dashed style={{ margin: '12px 0' }} />
                <Form.Item
                  name="newPassword"
                  label="New Password"
                  rules={[
                    { required: true, message: 'Required' },
                    { min: 8, message: 'At least 8 characters' },
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>
                <Form.Item
                  name="confirmPassword"
                  label="Confirm New Password"
                  dependencies={['newPassword']}
                  rules={[
                    { required: true, message: 'Required' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={changingPw}>
                  Change Password
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      </Spin>
    </PageLayout>
  );
}
